import { Link } from 'expo-router';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SectionHead } from '@/components/layout';
import { Button, Container, Pill, Text } from '@/components/ui';
import { EventPoster } from '@/components/marketing';
import { SeoHead } from '@/components/SeoHead';
import {
  useCalendar,
  useFeaturedEvents,
  useFocusDays,
  type CalendarEntry,
  type FocusDay,
} from '@/content/events';
import { useHover } from '@/lib/useHover';
import { MoodSection, fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

const webTransition = (props = 'background-color, color, transform, border-color', dur = '180ms') =>
  Platform.OS === 'web' ? ({ transitionProperty: props, transitionDuration: dur } as any) : null;

export default function EventsScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const featuredEvents = useFeaturedEvents();
  const focusDays = useFocusDays();
  const calendar = useCalendar();
  const heroStats = t('events.hero.stats', { returnObjects: true }) as Array<{
    label: string;
    value: string;
  }>;
  const whyCards = t('events.warum.cards', { returnObjects: true }) as Array<{
    kicker: string;
    title: string;
    body: string;
  }>;

  return (
    <View>
      <SeoHead
        title={t('events.seo.title')}
        description={t('events.seo.description')}
      />

      {/* Pagehead */}
      <View style={{ paddingTop: 48, paddingBottom: 48 }}>
        <Container>
          <View style={st.eyebrowRow}>
            <View style={[st.dot, { backgroundColor: theme.colors.accentAlt }]} />
            <Text variant="meta" tone="fgMute">{t('events.hero.eyebrow')}</Text>
          </View>
          <Text
            style={{
              fontFamily: theme.fontFamily.display,
              fontSize: fluidFontSize(48, 11.5, 96) as any,
              lineHeight: fluidLineHeight(0.95, 92) as any,
              color: theme.colors.fg,
              letterSpacing: -2,
            }}
          >
            {t('events.hero.titleLine1')}{'\n'}
            <Text
              italic
              style={{
                fontFamily: theme.fontFamily.displayItalic,
                fontStyle: 'italic',
                color: theme.colors.accent,
                fontSize: fluidFontSize(48, 11.5, 96) as any,
              }}
            >
              {t('events.hero.titleLine2')}
            </Text>
          </Text>
          <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 24, maxWidth: 680 }}>
            {t('events.hero.intro')}
          </Text>

          <View style={[st.statRow, { borderTopColor: theme.colors.rule, borderBottomColor: theme.colors.rule }]}>
            {heroStats.map((s, i, arr) => (
              <View
                key={s.label}
                style={[
                  st.stat,
                  i < arr.length - 1 && { borderRightColor: theme.colors.rule, borderRightWidth: 1 },
                ]}
              >
                <Text variant="eyebrow" tone="fgMute">{s.label}</Text>
                <Text
                  italic
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    fontSize: 44,
                    lineHeight: 48,
                    color: theme.colors.fg,
                    marginTop: 6,
                  }}
                >
                  {s.value}
                </Text>
              </View>
            ))}
          </View>
        </Container>
      </View>

      {/* Featured events */}
      {featuredEvents.map((event, i) => (
        <View key={event.slug} style={{ paddingVertical: 64 }}>
          <Container>
            <SectionHead
              idx={`0${i + 2} / ${t('events.featured.idxLabel')}`}
              title={event.title.split(' ')[0]}
              italic={event.title.split(' ').slice(1).join(' ')}
              sub={event.dateLabel}
            />
            <EventPoster event={event} />
          </Container>
        </View>
      ))}

      {/* 04 Focus Days */}
      <View style={{ paddingVertical: 96 }}>
        <Container>
          <SectionHead
            idx={t('events.focusDays.idx')}
            title={t('events.focusDays.title')}
            italic={t('events.focusDays.italic')}
            sub={t('events.focusDays.sub')}
          />
          <View style={st.focusWrap}>
            <View style={[st.focusInfo, { borderColor: theme.colors.rule, borderRadius: theme.radii.md, backgroundColor: theme.colors.bgSoft }]}>
              <Text variant="eyebrow" style={{ color: theme.colors.accent }}>{t('events.focusDays.info.schedule')}</Text>
              <Text variant="h3" style={{ marginTop: 12 }}>{t('events.focusDays.info.headline')}</Text>
              <Text variant="body" tone="fgSoft" style={{ marginTop: 12 }}>
                {t('events.focusDays.info.body')}
              </Text>
              <View style={{ marginTop: 24 }}>
                <Button variant="primary" size="md" trailingArrow href="/kontakt">
                  {t('events.focusDays.info.cta')}
                </Button>
              </View>
            </View>
            <View style={st.focusGrid}>
              {focusDays.map((d) => (
                <FocusCard key={d.date} d={d} />
              ))}
            </View>
          </View>
        </Container>
      </View>

      {/* 05 Voller Kalender */}
      <View style={{ paddingVertical: 64 }}>
        <Container>
          <SectionHead
            idx={t('events.kalender.idx')}
            title={t('events.kalender.title')}
            italic={t('events.kalender.italic')}
            sub={t('events.kalender.sub')}
          />
          <View style={{ borderTopWidth: 1, borderTopColor: theme.colors.rule }}>
            {calendar.map((c) => (
              <CalRow key={c.title} c={c} />
            ))}
          </View>
        </Container>
      </View>

      {/* 06 Warum Events — Midnight */}
      <MoodSection
        mood="midnight"
        style={[
          st.midnight,
          Platform.OS === 'web'
            ? ({ boxShadow: `0 0 80px -20px ${palette.wine}55, 0 24px 60px -24px #15082E80` } as any)
            : null,
        ]}
      >
        <Container>
          <SectionHead
            idx={t('events.warum.idx')}
            title={t('events.warum.title')}
            italic={t('events.warum.italic')}
            sub={t('events.warum.sub')}
            invert
          />
          <View style={st.whyGrid}>
            {whyCards.map((w) => (
              <View key={w.kicker} style={[st.whyCard, { borderColor: 'rgba(245,240,255,0.16)' }]}>
                <Text variant="eyebrow" style={{ color: palette.ember }}>{w.kicker}</Text>
                <Text style={{ fontFamily: 'InstrumentSerif_400Regular', fontSize: 32, lineHeight: 34, color: palette.paper, marginTop: 12 }}>
                  {w.title}
                </Text>
                <Text style={{ color: 'rgba(245,240,255,0.7)', fontSize: 15, lineHeight: 24, marginTop: 12 }}>
                  {w.body}
                </Text>
              </View>
            ))}
          </View>
        </Container>
      </MoodSection>
    </View>
  );
}

function FocusCard({ d }: { d: FocusDay }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        st.focusCard,
        {
          backgroundColor: d.next ? theme.colors.fg : theme.colors.bgSoft,
          borderRadius: theme.radii.md,
          borderColor: hovered ? (d.next ? palette.ember : theme.colors.accent) : 'transparent',
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -3 }] } : null,
      ]}
    >
      <Text variant="eyebrow" style={{ color: d.next ? palette.ember : theme.colors.fgMute }}>
        {d.next ? t('events.focusDays.card.labelNext') : t('events.focusDays.card.labelDefault')}
      </Text>
      <Text
        style={{
          fontFamily: 'InstrumentSerif_400Regular',
          fontSize: 28,
          lineHeight: 32,
          color: d.next ? palette.paper : theme.colors.fg,
          marginTop: 8,
        }}
      >
        {d.date}
      </Text>
      {d.topic ? (
        <Text variant="body" style={{ color: d.next ? 'rgba(245,240,255,0.78)' : theme.colors.fgSoft, marginTop: 8 }}>
          {d.topic}
        </Text>
      ) : null}
    </Pressable>
  );
}

function CalRow({ c }: { c: CalendarEntry }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { hovered, hoverProps } = useHover();
  const content = (
    <View
      style={[
        st.calRow,
        {
          borderBottomColor: theme.colors.rule,
          backgroundColor: c.highlighted
            ? theme.colors.bgSoft
            : hovered
              ? theme.colors.bgSoft
              : 'transparent',
          marginHorizontal: hovered ? -12 : 0,
          paddingHorizontal: hovered ? 12 : 0,
          borderRadius: hovered ? 8 : 0,
        },
        webTransition(),
      ]}
    >
      <View style={st.calDate}>
        <Text variant="meta" tone="fgMute">{c.date}</Text>
      </View>
      <View style={st.calTitle}>
        <Text variant="h4" style={{ color: c.highlighted ? theme.colors.accent : theme.colors.fg }}>
          {c.title}
        </Text>
        <Text variant="meta" tone="fgMute" style={{ marginTop: 4 }}>{c.format}</Text>
      </View>
      <View style={st.calCta}>
        {c.highlighted ? (
          <Pill variant="ember">{t('events.kalender.pillFeatured')}</Pill>
        ) : (
          <Pill variant="ghost">{t('events.kalender.pillReservation')}</Pill>
        )}
      </View>
    </View>
  );
  return c.url ? (
    <Link href={c.url as any} asChild>
      <Pressable accessibilityRole="link" {...(hoverProps as any)}>
        {content}
      </Pressable>
    </Link>
  ) : (
    <Pressable {...(hoverProps as any)}>{content}</Pressable>
  );
}

const st = StyleSheet.create({
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 32 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  statRow: {
    marginTop: 48,
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
    borderBottomWidth: 1,
  },
  stat: { flex: 1, minWidth: 200, paddingVertical: 24, paddingHorizontal: 24 },
  focusWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' },
  focusInfo: { flex: 1, minWidth: 280, padding: 32, borderWidth: 1 },
  focusGrid: { flexBasis: 320, flexGrow: 1, flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  focusCard: { flex: 1, minWidth: 140, padding: 20, borderWidth: 1 },
  calRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 22,
    borderBottomWidth: 1,
  },
  calDate: { flexBasis: 180, flexGrow: 0 },
  calTitle: { flex: 1, minWidth: 240 },
  calCta: { flexBasis: 140, flexGrow: 0, alignItems: 'flex-end' },
  midnight: {
    paddingVertical: 96,
    paddingHorizontal: 8,
    marginHorizontal: 24,
    marginVertical: 48,
    borderRadius: 12,
    overflow: 'hidden',
  },
  whyGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  whyCard: { flex: 1, minWidth: 280, padding: 32, borderWidth: 1, borderRadius: 12 },
});
